<div class="verification-results-container">
    <!-- Content Section -->
    <div class="collapse" :class="{ 'show': active_tabs.includes('Forms') }">
      <div class="mt-2">
        <div class="border-0 p-2 rounded-lg">

          <!-- Services Content - Only shown when dropdown is open -->
          <div v-if="is_trust_form_open" class="compliance-monitoring-projects-wrapper">

            <!-- Loading State -->
            <div v-if="loading_results" class="text-center p-4">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <span class="ms-2 text-xxs">Loading verification results...</span>
            </div>

            <!-- Error State -->
            <div v-else-if="error_message" class="alert alert-danger">
              {{ error_message }}
            </div>

            <!-- Results Section -->
            <div v-else-if="verification_results.length > 0">
              <div
                class="compliance-monitoring-project-accordion border border-2 rounded-lg mb-3 shadow"
                v-for="(service, index) in verification_results"
                :key="service.id"
                v-show="should_show_service(service)"
              >
                <div class="compliance-monitoring-project-header" @click="toggle_expanded(index)">
                  <div class="compliance-monitoring-project-connector"></div>
                  <div class="d-flex align-items-center">
                    <i class="bi" :class="expanded_index === index ? 'bi-chevron-down' : 'bi-chevron-right'"></i>
                    <div class="ms-2">
                      <i class="bi" :class="get_service_icon(service.name)"></i>
                    </div>
                    <div class="ms-3">
                      <div class="d-flex align-items-center gap-2 mb-1">
                        <p class="mb-0 text-xs">{{ service.name }}</p>
                        <span class="status-badge" :class="get_overall_status_class(service)">{{ get_overall_status(service) }}</span>
                      </div>
                      <p class="mb-0 text-xxs text-muted">Last updated: {{ get_last_updated() }}</p>
                    </div>
                  </div>
                </div>

                <!-- Detail Panel -->
                <div v-if="expanded_index === index" class="p-3">
                  
                  <!-- Service Status Message -->
                  <div class="alert" :class="get_alert_class(service)" role="alert">
                    <strong>{{ service.name }} Status:</strong> {{ get_service_message(service) }}
                  </div>

                  <!-- AML-Specific Display -->
                  <div v-if="is_aml_service(service) && service.aml_check_results" class="card border-0 rounded-4 bg-white p-4 w-100 mb-4">
                    <div class="bg-light border rounded-3 p-3 mb-3">
                      <AMLDashboardResults
                        :aml="service.aml_check_results"
                        :date="service.verification_date || get_last_updated()"
                      />
                      <!-- Detailed Matches -->
                      <div v-if="service.aml_check_results.total_matches > 0">
                        <p class="fw-bold text-xs text-dark mb-3">Detailed Matches</p>
                        <div
                          v-for="(match, idx) in service.aml_check_results.all_matches"
                          :key="idx"
                          class="card border mb-3 p-3 bg-white"
                        >
                          <div class="mb-3">
                            <h6 class="text-sm fw-bold mb-2">{{ match.entity_name }}</h6>
                            <span class="badge bg-success text-white text-xxs px-2 py-1 rounded">
                              {{ match.entity_type }}
                            </span>
                          </div>

                          <!-- Info Grid - Two Column Layout -->
                          <div class="row mb-3">
                            <div class="col-md-6 mb-2">
                              <div class="text-muted text-xxs mb-1">Category</div>
                              <div class="text-xs fw-normal">{{ match.category }}</div>
                            </div>

                            <div class="col-md-6 mb-2">
                              <div class="text-muted text-xxs mb-1">Date Listed</div>
                              <div class="text-xs fw-normal">{{ match.date_listed }}</div>
                            </div>

                            <div v-if="match.gender !== 'N/A'" class="col-md-6 mb-2">
                              <div class="text-muted text-xxs mb-1">Gender</div>
                              <div class="text-xs fw-normal">{{ match.gender }}</div>
                            </div>

                            <div v-if="match.date_of_birth !== 'N/A'" class="col-md-6 mb-2">
                              <div class="text-muted text-xxs mb-1">Date of Birth</div>
                              <div class="text-xs fw-normal">{{ match.date_of_birth }}</div>
                            </div>
                          </div>

                          <!-- Full Width Fields -->
                          <div v-if="match.occupation !== 'N/A'" class="mb-3">
                            <div class="text-muted text-xxs mb-1">Occupation</div>
                            <div class="text-xs fw-normal">{{ match.occupation }}</div>
                          </div>

                          <div class="mb-3">
                            <div class="text-muted text-xxs mb-1">Summary</div>
                            <div class="text-xs fw-normal">{{ match.summary }}</div>
                          </div>

                          <!-- Comments Section -->
                          <div v-if="match.comment" class="pt-3 border-top">
                            <div class="text-muted small mb-2 fw-semibold text-xs">
                              References & Supporting Documents
                            </div>

                            <div class="row g-2">
                              <div
                                v-for="(ref, idx) in parse_references(match.comment)"
                                :key="idx"
                                class="col-md-6"
                              >
                                <!-- URL -->
                                <a 
                                  v-if="ref.is_url"
                                  :href="ref.raw"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  class="d-flex gap-2 align-items-start p-2 rounded border bg-white text-info text-decoration-none w-100 text-xs"
                                >
                                  <i class="bi bi-link-45deg flex-shrink-0"></i>
                                    <span class="small text-break lh-sm">{{ ref.display }}</span>
                                </a>

                                <!-- Text -->
                                <div v-else class="small bg-light border rounded p-2 text-dark text-xs" style="line-height:1.4">
                                  {{ ref.display }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- No Matches Message -->
                      <div v-else class="text-center py-4">
                        <i class="bi bi-check-circle text-success fs-1"></i>
                        <p class="text-muted mt-2 mb-0 text-xs">
                          No AML matches found. This is a positive result.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Field Results (for non-AML services) -->
                  <div v-if="!is_aml_service(service) && has_field_results(service)" class="mb-4">
                    <h6 class="text-xs fw-bold mb-3">Field Verification Results</h6>
                    <div class="table-responsive">
                      <table class="table table-sm table-bordered">
                        <thead>
                          <tr>
                            <th class="text-xxs">Field</th>
                            <th class="text-xxs">User Answer</th>
                            <th class="text-xxs">Verified Value</th>
                            <th class="text-xxs">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(field, fieldName) in get_field_results(service)" :key="fieldName">
                            <td class="text-xxs">{{ formatKey(fieldName) }}</td>
                            <td class="text-xxs">{{ field.user_answer || 'N/A' }}</td>
                            <td class="text-xxs">{{ field.api_value || 'N/A' }}</td>
                            <td class="text-xxs">
                              <span class="badge" :class="field.match ? 'bg-success' : 'bg-danger'">
                                {{ field.match ? 'Match' : 'Mismatch' }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Detailed Results Data (for non-AML services) -->
                  <div v-if="!is_aml_service(service) && has_detailed_results(service)" class="mb-4 ">
                    <h6 class="text-xs fw-bold mb-3">Detailed Results</h6>
                    <div class="detailed-results-container mb-4 border p-3 rounded">
                      <div v-for="(section, sectionName) in get_detailed_results(service)" :key="sectionName" 
                           class="detailed-section mb-3">
                        <h6 class="text-xs fw-bold mb-3 pb-2 border-bottom">{{ formatKey(sectionName) }}</h6>
                        
                        <div v-if="Array.isArray(section) && section.length > 0" class="array-data">
                          <div v-for="(item, idx) in section" :key="idx" class="mb-3 pb-3 border-bottom">
                            <p class="text-xxs text-muted mb-2"><strong>Entry {{ idx + 1 }}</strong></p>
                            <div class="row g-2">
                              <div class="col-12" v-for="(value, key) in item" :key="key">
                                <div class="d-flex justify-content-between align-items-center py-1">
                                  <span class="text-xxs text-muted">{{ formatKey(key) }}:</span>
                                  <span class="text-xxs text-end ms-3">{{ formatValue(value) }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div v-else-if="isNestedObject(section)" class="nested-data">
                          <div class="row g-2">
                            <div class="col-12" v-for="(value, key) in section" :key="key">
                              <div class="d-flex justify-content-between align-items-center py-1 border-bottom">
                                <span class="text-xxs text-muted">{{ formatKey(key) }}:</span>
                                <span class="text-xxs text-end ms-3">{{ formatValue(value) }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div v-else class="text-xxs">
                          {{ formatValue(section) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Empty Results Message (for non-AML services) -->
                  <div v-if="!is_aml_service(service) && !has_field_results(service) && !has_detailed_results(service)" class="text-center p-3">
                    <i class="bi bi-info-circle fs-4 text-muted"></i>
                    <p class="text-muted mt-2 mb-0 text-xs">No detailed results available for this service.</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="text-center p-4 border rounded-lg mt-3 shadow">
              <i class="bi bi-inbox fs-1 text-muted"></i>
              <p class="text-muted mt-2 mb-0 text-xs">No verification results found.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>